// C:\platforma-frontend-admin-creator\app-subdomain-creators\src\main.js

import { createApp } from 'vue';
import { ref } from 'vue';
import App from './App.vue';
import './assets/tailwind.css';
import router from './router'; // Import router
import { installPlatformaConversionTracking } from './services/platformaConversionTracking';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import { createPinia } from 'pinia';


const app = createApp(App);
const CREATOR_AUTH_EXPIRED_EVENT = 'creator:auth-expired';
const showNotification = ref(false);
const isError = ref(false);
const notificationVariant = ref('success');
const notificationMessage = ref({
  title: '',
  body: '',
  avatarUrl: '',
  toastStyle: '',
  reactionType: '',
  postBadge: false,
  linkPath: '',
  linkLabel: '',
});
let notificationTimer = null;

const pinia = createPinia();

const isAuthStatusCode = (status) => {
  const code = Number.parseInt(String(status || 0), 10);
  return code === 401;
};

const resolveFetchUrl = (resource) => {
  if (typeof window === 'undefined') return null;
  try {
    if (resource instanceof Request) {
      return new URL(resource.url, window.location.origin);
    }
    return new URL(String(resource || ''), window.location.origin);
  } catch (_error) {
    return null;
  }
};

const shouldMonitorCreatorApiRequest = (resource) => {
  const url = resolveFetchUrl(resource);
  if (!url) return false;
  const path = String(url.pathname || '').toLowerCase();
  if (!path.startsWith('/api/')) return false;
  if (
    path === '/api/auth/login'
    || path === '/api/auth/login/mfa'
    || path === '/api/auth/logout'
    || path === '/api/auth/password-recovery/request'
    || path === '/api/auth/password-recovery/verify'
    || path === '/api/auth/password-recovery/reset'
  ) {
    return false;
  }
  return true;
};

const installCreatorAuthFetchGuard = () => {
  if (typeof window === 'undefined') return;
  if (window.__creatorAuthFetchGuardInstalled) return;
  window.__creatorAuthFetchGuardInstalled = true;
  window.__creatorAuthExpiredDispatched = false;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (shouldMonitorCreatorApiRequest(args?.[0]) && isAuthStatusCode(response?.status)) {
      if (!window.__creatorAuthExpiredDispatched) {
        window.__creatorAuthExpiredDispatched = true;
        window.dispatchEvent(new CustomEvent(CREATOR_AUTH_EXPIRED_EVENT, {
          detail: {
            reason: 'session-expired',
            status: Number(response.status || 0),
            path: resolveFetchUrl(args?.[0])?.pathname || '',
          },
        }));
      }
    }
    return response;
  };
};

installCreatorAuthFetchGuard();

// ?? Pinia plugin: auto-restore upload queue on startup
pinia.use((context) => {
  if (context.store.$id === 'mediaUpload') {
    const saved = localStorage.getItem('mediaUploadQueue');
    if (saved) {
      try {
        context.store.uploads = JSON.parse(saved);
      } catch (e) {
        console.error("Failed to restore persisted uploads:", e);
      }
    }
  }
});

app.use(pinia);

app.use(router);
installPlatformaConversionTracking(router);

// Register VueReCaptcha with your site key
app.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
});

app.config.globalProperties.$showNotification = showNotification;
app.config.globalProperties.$isError = isError;
app.config.globalProperties.$notificationVariant = notificationVariant;
app.config.globalProperties.$notificationMessage = notificationMessage;
app.config.globalProperties.$notify = ({
  title = 'Success',
  body = '',
  avatarUrl = '',
  toastStyle = '',
  reactionType = '',
  postBadge = false,
  linkPath = '',
  linkLabel = '',
  variant = 'success',
  duration = 10000,
  isError: forceError = null,
} = {}) => {
  if (notificationTimer) {
    clearTimeout(notificationTimer);
    notificationTimer = null;
  }

  notificationMessage.value = {
    title,
    body,
    avatarUrl,
    toastStyle,
    reactionType,
    postBadge,
    linkPath,
    linkLabel,
  };
  notificationVariant.value = variant;
  isError.value = forceError == null ? variant === 'error' : !!forceError;
  showNotification.value = true;

  notificationTimer = setTimeout(() => {
    showNotification.value = false;
    notificationTimer = null;
  }, Math.max(1000, Number(duration) || 10000));
};

if (typeof window !== 'undefined') {
  window.__creatorNotify = app.config.globalProperties.$notify;
}

app.mount('#app');
